import type { TFunction } from 'i18next';
import type { ComponentProps } from 'react';

import type { IAddress, TEmailVerificationStatus } from '@feathr/blackbox';
import type { Pill } from '@feathr/components';
import { escapeRegExp } from '@feathr/hooks';

type TPillTheme = ComponentProps<typeof Pill>['theme'];

const statusThemes = new Map<TEmailVerificationStatus, TPillTheme>([
  ['Success', 'green'],
  ['Pending', 'yellow'],
  ['Unverified', 'red'],
]);

/** The list filter for the search box: the typed text, matched anywhere in the email or the sender name. */
export function getSearchFilters(search?: string): Record<string, unknown> {
  const text = search?.trim();
  if (!text) {
    return {};
  }
  const pattern = escapeRegExp(text);
  return {
    __raw__: {
      $or: [
        { email: { $regex: pattern, $options: 'i' } },
        { from_name: { $regex: pattern, $options: 'i' } },
      ],
    },
  };
}

/** The pill color for a status. Any other status the backend sends gets the default color. */
export function getStatusTheme(status: TEmailVerificationStatus): TPillTheme {
  return statusThemes.get(status);
}

/** The pill label for a status. Any other status the backend sends reads as "Unverified". */
export function getStatusLabel(status: TEmailVerificationStatus, t: TFunction): string {
  if (status === 'Success') {
    return t('Verified');
  }
  if (status === 'Pending') {
    return t('Pending');
  }
  return t('Unverified');
}

/** A saved address can be an object whose fields are all empty. */
export function hasAddress(address?: IAddress | null): address is IAddress {
  return !!address && Object.values(address).some(Boolean);
}
